import { useMemo, useRef, useState } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { isValidEmail } from "@utils";

// One header row of the email preview ("To:", "Cc:", "Bcc:"), in the mail-client style: an always-live
// field with a text cursor on hover and an underline while focused. Committed addresses render as pills
// with an X; the input after them is both a search over `suggestions` and a place to type an address.
// Picking a suggestion, or Enter / comma / blur on a valid typed address, adds a pill. `suggestions` is a
// list of { label, addresses: [{ email, name? }] } groups shown in order; addresses in `excluded`
// (already on any line of the email) are hidden so nothing is added twice. `onEditingChange` reports
// uncommitted typed text so the modal can hold Send until it is resolved.
export default function RecipientListRow({
  label,
  addresses,
  onChange,
  max,
  minimum = 0,
  emptyText = "",
  disabled = false,
  suggestions = [],
  excluded = new Set(),
  onEditingChange,
}) {
  const [query, setQuery] = useState("");
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const name = label.replace(/:$/, "");
  const full = addresses.length >= max;
  const has = (address) =>
    addresses.some((a) => a.toLowerCase() === address.toLowerCase()) ||
    [...excluded].some((a) => a.toLowerCase() === address.toLowerCase());

  // Uncommitted text is what holds Send — a pill is committed, typed text is not.
  const setTyped = (next) => {
    setQuery(next);
    onEditingChange?.(next.trim().length > 0);
  };

  const clear = () => {
    setTyped("");
    setError(null);
  };

  const add = (address) => {
    const trimmed = (address ?? "").trim();
    if (!trimmed) return true;
    if (!isValidEmail(trimmed)) {
      setError(`"${trimmed}" is not a valid email address.`);
      return false;
    }
    if (has(trimmed)) {
      setError(`${trimmed} is already on the email.`);
      return false;
    }
    if (addresses.length >= max) {
      setError(`At most ${max} addresses.`);
      return false;
    }
    onChange([...addresses, trimmed]);
    clear();
    return true;
  };

  const remove = (address) => onChange(addresses.filter((a) => a !== address));
  const selected = useMemo(() => addresses.map((email) => ({ email })), [addresses]);
  const sameAddress = (a, b) => (a?.email ?? "").toLowerCase() === (b?.email ?? "").toLowerCase();
  const canRemove = !disabled && addresses.length > minimum;

  // The dropdown: each group's addresses not already on the email, filtered by the typed text against
  // name or address; plus a "use what you typed" entry when the text is a new, well-formed address.
  const needle = query.trim().toLowerCase();
  const groups = useMemo(
    () =>
      suggestions
        .map((group) => ({
          label: group.label,
          addresses: (group.addresses ?? []).filter(
            (option) =>
              option.email &&
              !has(option.email) &&
              (!needle ||
                option.email.toLowerCase().includes(needle) ||
                (option.name ?? "").toLowerCase().includes(needle)),
          ),
        }))
        .filter((group) => group.addresses.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suggestions, needle, addresses, excluded],
  );
  const typedIsNew =
    needle.length > 0 &&
    isValidEmail(query.trim()) &&
    !has(query.trim()) &&
    !groups.some((group) => group.addresses.some((o) => o.email.toLowerCase() === needle));

  const pill = (address) => (
    <span
      key={address}
      className="inline-flex max-w-full items-center gap-1 rounded-full bg-zinc-100 py-0.5 pl-2.5 pr-1 text-xs text-zinc-700"
    >
      <span className="truncate">{address}</span>
      {/* Removal runs on mousedown, before the input blurs and the combobox's outside-click handling
          re-renders the row — a plain click was lost while the field had focus. Preventing default also
          keeps focus in the input, so nothing typed there is committed by a blur. */}
      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (canRemove) remove(address);
        }}
        onClick={(e) => e.stopPropagation()}
        disabled={!canRemove}
        title={canRemove ? `Remove ${address}` : `At least ${minimum} ${name} address is needed`}
        aria-label={`Remove ${address}`}
        className="rounded-full p-0.5 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <XMarkIcon className="size-3" />
      </button>
    </span>
  );

  return (
    <div className="flex items-start px-2 py-1">
      <span className="w-20 shrink-0 pt-1 text-zinc-400">{label}</span>
      <div className="min-w-0 flex-1">
        {/* Multi-select so the list stays open across picks while the field has focus (single-select
            closes after each pick); the selected value mirrors the pills, and a pick is whatever is
            new in the list Headless hands back. */}
        <Combobox
          multiple
          immediate
          disabled={disabled}
          value={selected}
          by={sameAddress}
          onChange={(list) => {
            const picked = (list ?? []).find((option) => option?.email && !has(option.email));
            if (picked) add(picked.email);
          }}
        >
          {/* The field: pills then the input, with a text cursor on hover and an underline only while it
              holds focus, so the header reads as plain text until the user reaches for it. */}
          <div
            onClick={() => inputRef.current?.focus()}
            className="flex min-w-0 flex-wrap items-center gap-1.5 border-b border-transparent px-0.5 py-0.5 cursor-text focus-within:border-primary-500"
          >
            {addresses.map(pill)}
            {full ? (
              <span className="px-1 text-[11px] text-zinc-400">Limit of {max} addresses reached.</span>
            ) : (
              <ComboboxInput
                ref={inputRef}
                value={query}
                aria-label={`${name} recipients`}
                placeholder={addresses.length === 0 ? emptyText : ""}
                onChange={(e) => {
                  setTyped(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "," || (e.key === "Enter" && typedIsNew && groups.length === 0)) {
                    e.preventDefault();
                    add(query);
                  }
                  if (e.key === "Escape") clear();
                  // Backspace on an empty input removes the last pill, as tag inputs do.
                  if (e.key === "Backspace" && !query && canRemove && addresses.length > 0) {
                    remove(addresses[addresses.length - 1]);
                  }
                }}
                onBlur={() => {
                  if (query.trim()) add(query);
                }}
                className="min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-0.5 text-xs text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-0 disabled:cursor-default"
              />
            )}
          </div>
          <ComboboxOptions
            anchor="bottom start"
            className="z-[70] max-h-56 min-w-[18rem] overflow-auto rounded-lg border border-zinc-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 [--anchor-gap:4px] focus:outline-none empty:hidden"
          >
            {groups.map((group) => (
              <div key={group.label}>
                <div className="px-2 pb-0.5 pt-1.5 text-[10px] font-medium uppercase tracking-wide text-zinc-400">
                  {group.label}
                </div>
                {group.addresses.map((option) => (
                  <ComboboxOption
                    key={option.email}
                    value={option}
                    className="flex cursor-pointer items-baseline gap-2 rounded px-2 py-1.5 data-[focus]:bg-zinc-50"
                  >
                    {option.name && <span className="truncate text-zinc-800">{option.name}</span>}
                    <span className="truncate text-zinc-500">{option.email}</span>
                  </ComboboxOption>
                ))}
              </div>
            ))}
            {typedIsNew && (
              <ComboboxOption
                value={{ email: query.trim() }}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-zinc-700 data-[focus]:bg-zinc-50"
              >
                Add <span className="font-medium">{query.trim()}</span>
              </ComboboxOption>
            )}
          </ComboboxOptions>
        </Combobox>
        {error && <div className="mt-1 text-[11px] text-red-600">{error}</div>}
      </div>
    </div>
  );
}
